import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, Button, Form, Badge, Alert, Nav, Tab } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useBranchAPI } from '../../hooks';
import { shiftPreferenceAPI } from '../../services';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import ShiftPreferenceWeeklyGrid from './ShiftPreferenceWeeklyGrid';
import { toast } from 'react-toastify';

const weekdayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const weekdayNames = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

/**
 * #191439 — Màn này là ĐÍCH của link sâu trong 3 tin ca ưu tiên (dòng 105, 106, 111 của bảng
 * khảo sát #132320): copy ca ưu tiên tuần trước, NV đăng ký ca ưu tiên mới, xin off tuần sau.
 * Riêng dòng 111 có mốc "quá hạn 00:00 T6 → TỰ ĐỘNG DUYỆT", nên mở đúng người ngay từ link là
 * chuyện mất/không mất quyền quyết định, không phải tiện tay.
 *
 * `q` ở đây lọc PHÍA TRÌNH DUYỆT: API `getShiftPreferences` không có tham số tìm theo tên,
 * nhưng trang vốn đã tải sẵn cả danh sách (`limit: 1000`) rồi tự gom nhóm theo nhân viên —
 * nên lọc trên dữ liệu đã có là đủ và KHÔNG thêm lời gọi API nào.
 */
/**
 * #190405 (lệnh sếp update_id=731599481, 02/09/2026): thêm/xoá/sửa ca ưu tiên KHÔNG còn bước
 * duyệt — NV lưu là có hiệu lực ngay, đổi lại phải nhập lý do. Vì vậy màn này không còn là
 * "hàng chờ duyệt": nó là chỗ quản lý ĐỌC LẠI ai đổi gì, lúc nào, vì sao, và vẫn chỉnh trực
 * tiếp được ca ưu tiên của NV (kèm lý do).
 *
 * Nút Duyệt/Từ chối chỉ còn hiện trên các bản ghi CŨ còn kẹt `PENDING` từ trước ngày cắt
 * (đo trên production 02/09/2026: 6 bản ghi) — xử lý xong là biến mất, không để lại menu duyệt
 * rỗng.
 */
const SHIFT_PREFERENCE_FILTER_SPEC = {
  status: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => ['', 'PENDING', 'APPROVED'].includes(v.toUpperCase()),
  },
  q: { default: '', debounce: 350 },
  /**
   * #230650 (#191801): tab "Lưới tuần" mở MẶC ĐỊNH — nhưng 3 link Telegram sẵn có chỉ gửi `?q=`
   * (không gửi `tab`) và phải mở đúng "Theo nhân viên" như #191439 ⇒ mặc định phụ thuộc `q`
   * đang có trên URL.
   */
  tab: {
    default: () =>
      new URLSearchParams(window.location.search).get('q') ? 'employee' : 'luoi-tuan',
    valid: (v) => ['luoi-tuan', 'employee', 'template', 'change-requests'].includes(v),
  },
  // #230650: ô đang chọn ở tab Lưới tuần — `<templateId>-<thứ 0..6>`.
  o: { default: '' },
  // #171710 (YC-123608 US-4): ứng viên đang xem trên Lưới tuần — tô các ô người đó đã chọn.
  ung_vien: { default: '' },
};

export default function ShiftPreferenceReviewPage() {
  const { selectedBranch } = useBranch();
  const { withBranch } = useBranchAPI();


  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] = useFilterParams(
    SHIFT_PREFERENCE_FILTER_SPEC,
  );
  const activeTab = urlFilters.tab;
  const setActiveTab = (tab) => setUrlFilters({ tab });
  const nameQuery = urlFilters.q;
  const [templateCounts, setTemplateCounts] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // #190405: bỏ ô chọn trạng thái ở đầu trang (mọi ca ưu tiên đều có hiệu lực ngay), nhưng vẫn
  // đọc `?status=` từ URL để các link sâu cũ không gãy.
  const statusFilter = urlFilters.status;

  // Change request state
  const [changeRequests, setChangeRequests] = useState([]);
  const [loadingCRs, setLoadingCRs] = useState(false);
  const [crStatusFilter, setCrStatusFilter] = useState('');
  const [selectedCRIds, setSelectedCRIds] = useState(new Set());
  const [notesByCRId, setNotesByCRId] = useState({});
  const [savingCR, setSavingCR] = useState(false);
  // #190405: quản lý chỉnh ca ưu tiên của NV cũng phải nêu lý do (server bắt buộc).
  const [adminReason, setAdminReason] = useState('');

  const loadPreferences = async () => {
    if (!selectedBranch?.id) return;
    setLoading(true);
    try {
      const res = await withBranch(shiftPreferenceAPI.getShiftPreferences, {
        branch_id: selectedBranch.id,
        status: statusFilter || '',
        page: 1,
        limit: 1000,
      });
      const data = res.data || res || [];
      const mapped = data.map((p) => ({
        ...p,
        daysOfWeekEdited: Array.from(new Set(p.days_of_week || [])).sort(),
        selected: true,
      }));
      setPreferences(mapped);
      setBranchName(selectedBranch.name || '');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Không thể tải danh sách ca ưu tiên');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateCounts = async () => {
    if (!selectedBranch?.id) return;
    setLoadingTemplates(true);
    try {
      const res = await shiftPreferenceAPI.getTemplateRegistrationCounts(selectedBranch.id);
      const data = res.data || res || [];
      setTemplateCounts(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Không thể tải thống kê ca mẫu');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadChangeRequests = async () => {
    if (!selectedBranch?.id) return;
    setLoadingCRs(true);
    try {
      const data = await shiftPreferenceAPI.getChangeRequests({
        branch_id: selectedBranch.id,
        status: crStatusFilter || '',
      });
      setChangeRequests(data || []);
      setSelectedCRIds(new Set());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Không thể tải lịch sử thay đổi ca ưu tiên');
    } finally {
      setLoadingCRs(false);
    }
  };

  useEffect(() => {
    loadPreferences();
    loadTemplateCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch?.id, statusFilter]);

  useEffect(() => {
    if (activeTab === 'change-requests') loadChangeRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBranch?.id, crStatusFilter]);

  // #191439: lọc theo tên NV lấy từ `?q=`. So khớp không phân biệt hoa/thường và bỏ dấu cách
  // thừa, vì tên trong link đến từ dữ liệu server còn người dùng có thể tự gõ lại.
  const visiblePreferences = useMemo(() => {
    const needle = (nameQuery || '').trim().toLowerCase();
    if (!needle) return preferences;
    return preferences.filter((p) => {
      const emp = p.employee || {};
      return `${emp.full_name ?? ''} ${emp.username ?? ''}`
        .toLowerCase()
        .includes(needle);
    });
  }, [preferences, nameQuery]);

  const groupedByEmployee = useMemo(() => {
    const map = {};
    visiblePreferences.forEach((p) => {
      const emp = p.employee || {};
      const key = emp.id || p.employee_id || 'unknown';
      if (!map[key]) {
        map[key] = {
          employee: emp,
          items: [],
        };
      }
      map[key].items.push(p);
    });
    return Object.values(map);
  }, [visiblePreferences]);

  const groupedByTemplate = useMemo(() => {
    const map = {};
    visiblePreferences.forEach((p) => {
      const templateId = p.shift_template_id || p.shift_template?.id || 'unknown';
      if (!map[templateId]) {
        const templateInfo = templateCounts.find((t) => t.id === templateId) || p.shift_template || {};
        map[templateId] = {
          template: templateInfo,
          items: [],
        };
      }
      map[templateId].items.push(p);
    });
    return Object.values(map);
  }, [visiblePreferences, templateCounts]);

  const toggleDay = (prefId, day) => {
    setPreferences((prev) =>
      prev.map((p) => {
        if (p.id !== prefId) return p;
        const has = p.daysOfWeekEdited.includes(day);
        const days = has ? p.daysOfWeekEdited.filter((d) => d !== day) : [...p.daysOfWeekEdited, day];
        return { ...p, daysOfWeekEdited: days.sort() };
      }),
    );
  };

  const toggleSelect = (prefId) => {
    setPreferences((prev) => prev.map((p) => (p.id === prefId ? { ...p, selected: !p.selected } : p)));
  };

  const handleApproveSingle = async (prefId) => {
    const pref = preferences.find((p) => p.id === prefId);
    if (!pref) return;

    const item = {
      id: pref.id,
      days_of_week: pref.daysOfWeekEdited,
    };

    if (!adminReason.trim()) {
      toast.warn('Vui lòng nhập lý do chỉnh ca ưu tiên');
      return;
    }

    setSaving(true);
    try {
      await withBranch(shiftPreferenceAPI.adminReviewBatch, {
        branch_id: selectedBranch.id,
        items: [item],
        reason: adminReason.trim(),
      });
      toast.success('Đã áp dụng thay đổi ca ưu tiên');
      await loadPreferences();
      await loadTemplateCounts();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Áp dụng thay đổi thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveBatch = async () => {
    const items = preferences
      .filter((p) => p.selected)
      .map((p) => ({
        id: p.id,
        days_of_week: p.daysOfWeekEdited,
      }));
    if (!items.length) {
      toast.warn('Vui lòng chọn ít nhất một ca ưu tiên');
      return;
    }
    if (!adminReason.trim()) {
      toast.warn('Vui lòng nhập lý do chỉnh ca ưu tiên');
      return;
    }
    setSaving(true);
    try {
      await withBranch(shiftPreferenceAPI.adminReviewBatch, {
        branch_id: selectedBranch.id,
        items,
        reason: adminReason.trim(),
      });
      toast.success('Đã áp dụng thay đổi ca ưu tiên');
      await loadPreferences();
      await loadTemplateCounts();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Áp dụng thay đổi thất bại');
    } finally {
      setSaving(false);
    }
  };


  const toggleCRSelect = (crId) => {
    setSelectedCRIds((prev) => {
      const next = new Set(prev);
      next.has(crId) ? next.delete(crId) : next.add(crId);
      return next;
    });
  };

  const handleSingleReviewCR = async (crId, action) => {
    setSavingCR(true);
    try {
      await shiftPreferenceAPI.reviewChangeRequest(crId, {
        action,
        note: notesByCRId[crId] || undefined,
      });
      toast.success(action === 'APPROVE' ? 'Đã duyệt yêu cầu' : 'Đã từ chối yêu cầu');
      await loadChangeRequests();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Thao tác thất bại');
    } finally {
      setSavingCR(false);
    }
  };

  const handleBulkReviewCR = async (action) => {
    if (!selectedCRIds.size) { toast.warn('Vui lòng chọn ít nhất một yêu cầu'); return; }
    setSavingCR(true);
    const ids = Array.from(selectedCRIds);
    let successCount = 0;
    for (const crId of ids) {
      try {
        await shiftPreferenceAPI.reviewChangeRequest(crId, {
          action,
          note: notesByCRId[crId] || undefined,
        });
        successCount++;
      } catch { /* bỏ qua lỗi từng item, log tổng kết */ }
    }
    toast.success(`${action === 'APPROVE' ? 'Duyệt' : 'Từ chối'} ${successCount}/${ids.length} yêu cầu thành công`);
    setSavingCR(false);
    await loadChangeRequests();
  };

  const renderDiff = (cr) => {
    const payload = cr.payload || {};
    // #190405: bản ghi mới có ảnh chụp before/after — bản ghi xoá thì đó là chỗ DUY NHẤT còn
    // giữ nội dung ca đã mất (khoá ngoại `preference_id` bị set null khi ca bị xoá).
    const before = payload.before || null;
    const after = payload.after || null;
    if (cr.type === 'DELETE') {
      return (
        <Alert variant="danger" className="py-1 mb-0 small">
          🗑️ Đã XOÁ ca ưu tiên
          {before?.shift_template_name && (
            <>
              {': '}
              <b>{before.shift_template_name}</b>
              {before.days_of_week?.length > 0 &&
                ` (${before.days_of_week.map((d) => weekdayLabels[d]).join(' ')})`}
            </>
          )}
        </Alert>
      );
    }
    if (cr.type === 'CREATE') {
      return (
        <Alert variant="success" className="py-1 mb-0 small">
          ➕ Thêm ca ưu tiên
          {after?.shift_template_name && (
            <>
              {': '}
              <b>{after.shift_template_name}</b>
              {after.days_of_week?.length > 0 &&
                ` (${after.days_of_week.map((d) => weekdayLabels[d]).join(' ')})`}
            </>
          )}
        </Alert>
      );
    }
    if (before && after) {
      return (
        <div className="small">
          <span className="text-muted">Ngày:</span>{' '}
          <s className="text-danger">
            {(before.days_of_week || []).map((d) => weekdayLabels[d]).join(' ') || '(trống)'}
          </s>
          {' → '}
          <b className="text-success">
            {(after.days_of_week || []).map((d) => weekdayLabels[d]).join(' ') || '(trống)'}
          </b>
          {before.shift_template_name !== after.shift_template_name && (
            <div>
              <span className="text-muted">Ca mẫu:</span>{' '}
              <s className="text-danger">{before.shift_template_name ?? '?'}</s>
              {' → '}
              <b className="text-success">{after.shift_template_name ?? '?'}</b>
            </div>
          )}
          {before.is_active !== after.is_active && (
            <div>
              <span className="text-muted">Trạng thái:</span>{' '}
              {after.is_active ? <b className="text-success">Bật lại</b> : <b className="text-danger">Tắt</b>}
            </div>
          )}
        </div>
      );
    }
    const items = [];
    if (payload.days_of_week) {
      const oldDays = (cr.preference?.days_of_week || []).map((d) => weekdayLabels[d]).join(' ');
      const newDays = payload.days_of_week.map((d) => weekdayLabels[d]).join(' ');
      items.push(
        <div key="days" className="small">
          <span className="text-muted">Ngày:</span>{' '}
          <s className="text-danger">{oldDays || '(trống)'}</s>{' → '}
          <b className="text-success">{newDays || '(trống)'}</b>
        </div>,
      );
    }
    if (payload.shift_template_id && cr.new_template) {
      const oldName = cr.preference?.shift_template?.name ?? '?';
      items.push(
        <div key="tpl" className="small">
          <span className="text-muted">Ca mẫu:</span>{' '}
          <s className="text-danger">{oldName}</s>{' → '}
          <b className="text-success">{cr.new_template.name}</b>
        </div>,
      );
    }
    if (payload.is_active !== undefined) {
      items.push(
        <div key="active" className="small">
          <span className="text-muted">Trạng thái:</span>{' '}
          {payload.is_active ? <b className="text-success">Bật lại</b> : <b className="text-danger">Tắt</b>}
        </div>,
      );
    }
    return items.length ? <>{items}</> : <span className="text-muted small">Không có thay đổi cụ thể</span>;
  };

  const pendingCRCount = changeRequests.filter((c) => c.status === 'PENDING').length;

  return (
    <PageLayout>
      <div className="mb-4">
        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />
        <Row className="align-items-center mb-3">
          <Col>
            <h2 className="mb-0">Ca ưu tiên & lịch sử thay đổi - {branchName}</h2>
            {activeTab === 'luoi-tuan' ? (
              <div className="text-muted">
                Nhìn 1 lần thấy thứ nào, giờ nào, ca nào đang ít người đăng ký ưu tiên.
              </div>
            ) : (
            <div className="text-muted">
              NV thêm/xoá/sửa ca ưu tiên có hiệu lực ngay, không còn bước duyệt — kèm lý do bắt
              buộc. Quản lý xem lại ở tab &quot;Lịch sử thay đổi&quot;, hoặc chỉnh trực tiếp ở đây
              (cũng phải nêu lý do).
            </div>
            )}
          </Col>
          {/* #230650: tab Lưới tuần chỉ đọc — ẩn ô tìm / lý do chỉnh / nút lưu của các tab cũ. */}
          {activeTab !== 'luoi-tuan' && (
          <Col xs="auto">
            <Form.Control
              size="sm"
              placeholder="Tìm theo tên nhân viên"
              value={nameQuery}
              onChange={(e) => setUrlFilters({ q: e.target.value })}
              style={{ inlineSize: '220px', display: 'inline-block' }}
              className="me-2"
            />
            <Form.Control
              size="sm"
              placeholder="Lý do chỉnh (bắt buộc)"
              value={adminReason}
              onChange={(e) => setAdminReason(e.target.value)}
              maxLength={500}
              style={{ inlineSize: '240px', display: 'inline-block' }}
              className="me-2"
            />
            <Button
              variant="outline-secondary"
              onClick={() => {
                loadPreferences();
                loadTemplateCounts();
              }}
              disabled={loading || saving || loadingTemplates}
              className="me-2"
            >
              Làm mới
            </Button>
            <Button
              variant="primary"
              onClick={handleApproveBatch}
              disabled={saving || loading || !preferences.length}
            >
              {saving ? 'Đang lưu...' : 'Lưu thay đổi (áp dụng ngay)'}
            </Button>
          </Col>
          )}
        </Row>

        <Tab.Container
          activeKey={activeTab}
          onSelect={setActiveTab}
        >
          <Nav
            variant="tabs"
            className="mb-4"
          >
            <Nav.Item>
              <Nav.Link eventKey="luoi-tuan">
                Lưới tuần{' '}
                <Badge bg="danger" pill>
                  MỚI
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="employee">Theo nhân viên ({groupedByEmployee.length})</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="template">Theo ca mẫu ({groupedByTemplate.length})</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="change-requests">
                Lịch sử thay đổi{' '}
                {/* Badge chỉ đếm DỮ LIỆU CŨ còn kẹt chờ duyệt — thao tác mới không sinh ra nữa. */}
                {pendingCRCount > 0 && (
                  <Badge bg="warning" text="dark" title="Bản ghi cũ chưa xử lý">
                    {pendingCRCount} cũ
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="luoi-tuan">
              {activeTab === 'luoi-tuan' && (
                <ShiftPreferenceWeeklyGrid
                  branchId={selectedBranch?.id}
                  cellParam={urlFilters.o}
                  onSelectCell={(o) => setUrlFilters({ o })}
                  applicantId={urlFilters.ung_vien}
                  onSelectApplicant={(id) => setUrlFilters({ ung_vien: id })}
                />
              )}
            </Tab.Pane>
            <Tab.Pane eventKey="employee">
              {!loading && !preferences.length && (
                <Alert variant="info">Chi nhánh này chưa có ca ưu tiên nào.</Alert>
              )}

              {loading && (
                <div className="text-center py-5">
                  <div
                    className="spinner-border"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {!loading &&
                groupedByEmployee.map((group) => (
                  <Card
                    key={group.employee.id || group.items[0].employee_id}
                    className="mb-3"
                  >
                    <Card.Body>
                      <h5 className="mb-3">
                        {group.employee.full_name || group.employee.username || 'Nhân viên'}{' '}
                        <Badge bg="secondary">{group.items.length} ca ưu tiên</Badge>
                      </h5>
                      <div className="d-flex flex-column gap-3">
                        {group.items.map((p) => (
                          <Row
                            key={p.id}
                            className="align-items-center"
                          >
                            <Col md={4}>
                              <Form.Check
                                type="checkbox"
                                id={`pref-${p.id}`}
                                label={
                                  <span>
                                    {p.shift_template?.name || 'Ca ưu tiên'}{' '}
                                    <Badge
                                      bg="info"
                                      className="ms-1"
                                    >
                                      Template
                                    </Badge>
                                  </span>
                                }
                                checked={p.selected}
                                onChange={() => toggleSelect(p.id)}
                              />
                              <div className="text-muted small mt-1">
                                {p.shift_template
                                  ? `${p.shift_template.start_hour || 0}h${String(
                                      p.shift_template.start_minute || 0,
                                    ).padStart(2, '0')} • ${p.shift_template.duration_minutes || 0} phút`
                                  : null}
                              </div>
                            </Col>
                            <Col md={8}>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {weekdayLabels.map((label, idx) => (
                                  <Button
                                    key={label}
                                    size="sm"
                                    variant={p.daysOfWeekEdited.includes(idx) ? 'primary' : 'outline-secondary'}
                                    onClick={() => toggleDay(p.id, idx)}
                                    disabled={!p.selected}
                                    title={weekdayNames[idx]}
                                  >
                                    {label}
                                  </Button>
                                ))}
                                {!p.daysOfWeekEdited.length && (
                                  <span className="text-danger small ms-2">
                                    Mảng ngày rỗng → ca này sẽ bị XOÁ khi lưu
                                  </span>
                                )}
                                <Button
                                  size="sm"
                                  variant="success"
                                  className="ms-2"
                                  onClick={() => handleApproveSingle(p.id)}
                                  disabled={saving || loading}
                                >
                                  Lưu ca này
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
            </Tab.Pane>

            <Tab.Pane eventKey="template">
              {loadingTemplates && (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {!loadingTemplates &&
                groupedByTemplate.map((group) => {
                  const template = group.template;
                  const templateId = template.id || group.items[0]?.shift_template_id;
                  const availabilityColor = template.availability_color || 'secondary';
                  const maxEmployees = template.max_employees || 0;
                  // Parse số lượng đăng ký theo ngày từ API (có thể là array hoặc object)
                  let dayCounts = {};
                  const rawDayCounts =
                    template.registration_counts_by_day || template.day_counts || template.daily_counts;
                  if (Array.isArray(rawDayCounts)) {
                    // Nếu là array: [{day: 1, registration_count: 5}, ...]
                    rawDayCounts.forEach((item) => {
                      if (item.day !== undefined && item.registration_count !== undefined) {
                        dayCounts[item.day] = item.registration_count;
                      }
                    });
                  } else if (rawDayCounts && typeof rawDayCounts === 'object') {
                    // Nếu là object: {1: 5, 2: 3, ...}
                    dayCounts = rawDayCounts;
                  }

                  return (
                    <Card
                      key={templateId}
                      className="mb-3"
                    >
                      <Card.Header
                        className={`d-flex justify-content-between align-items-center ${
                          availabilityColor === 'red'
                            ? 'bg-danger bg-opacity-10'
                            : availabilityColor === 'yellow'
                            ? 'bg-warning bg-opacity-10'
                            : availabilityColor === 'green'
                            ? 'bg-success bg-opacity-10'
                            : ''
                        }`}
                      >
                        <div className="flex-grow-1">
                          <h5 className="mb-1">
                            {template.name || 'Ca mẫu'}{' '}
                            <Badge
                              bg={
                                availabilityColor === 'red'
                                  ? 'danger'
                                  : availabilityColor === 'yellow'
                                  ? 'warning'
                                  : 'success'
                              }
                            >
                              {availabilityColor === 'red'
                                ? '🔴 Đỏ'
                                : availabilityColor === 'yellow'
                                ? '🟡 Vàng'
                                : '🟢 Xanh'}
                            </Badge>
                          </h5>
                          <div className="text-muted small mb-2">
                            {template.start_hour !== undefined
                              ? `${template.start_hour}h${String(template.start_minute || 0).padStart(2, '0')} • ${
                                  template.duration_minutes || 0
                                } phút`
                              : null}
                            {maxEmployees > 0 && ` • Tối đa ${maxEmployees} người`}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="text-muted small">Số lượng đăng ký theo ngày:</span>
                            {weekdayLabels.map((label, idx) => {
                              const count = dayCounts[idx] || 0;
                              const dayColor =
                                count >= 5 ? 'danger' : count >= 3 ? 'warning' : count > 0 ? 'success' : 'secondary';
                              return (
                                <Badge
                                  key={label}
                                  bg={dayColor}
                                  className="d-flex align-items-center gap-1"
                                >
                                  {label}: {count}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                        <div className="text-end ms-3">
                          <div className="text-muted small">{group.items.length} ca ưu tiên</div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex flex-column gap-3">
                          {group.items.map((p) => (
                            <Row
                              key={p.id}
                              className="align-items-center"
                            >
                              <Col md={4}>
                                <Form.Check
                                  type="checkbox"
                                  id={`pref-tpl-${p.id}`}
                                  label={
                                    <span>
                                      {p.employee?.full_name || p.employee?.username || 'Nhân viên'}{' '}
                                      <Badge
                                        bg="secondary"
                                        className="ms-1"
                                      >
                                        Nhân viên
                                      </Badge>
                                    </span>
                                  }
                                  checked={p.selected}
                                  onChange={() => toggleSelect(p.id)}
                                />
                              </Col>
                              <Col md={8}>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  {weekdayLabels.map((label, idx) => (
                                    <Button
                                      key={label}
                                      size="sm"
                                      variant={p.daysOfWeekEdited.includes(idx) ? 'primary' : 'outline-secondary'}
                                      onClick={() => toggleDay(p.id, idx)}
                                      disabled={!p.selected}
                                      title={weekdayNames[idx]}
                                    >
                                      {label}
                                    </Button>
                                  ))}
                                  {!p.daysOfWeekEdited.length && (
                                    <span className="text-danger small ms-2">
                                      Mảng ngày rỗng → ca này sẽ bị XOÁ khi lưu
                                    </span>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="success"
                                    className="ms-2"
                                    onClick={() => handleApproveSingle(p.id)}
                                    disabled={saving || loading}
                                  >
                                    Lưu ca này
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
            </Tab.Pane>
            <Tab.Pane eventKey="change-requests">
              {/* Toolbar */}
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <Form.Select
                  size="sm"
                  value={crStatusFilter}
                  onChange={(e) => setCrStatusFilter(e.target.value)}
                  style={{ inlineSize: '180px' }}
                >
                  <option value="">Toàn bộ lịch sử</option>
                  <option value="PENDING">Dữ liệu cũ chưa xử lý</option>
                  <option value="APPROVED">Đã áp dụng</option>
                  <option value="REJECTED">Đã từ chối (cũ)</option>
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={loadChangeRequests}
                  disabled={loadingCRs || savingCR}
                >
                  Làm mới
                </Button>
                {selectedCRIds.size > 0 && (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleBulkReviewCR('APPROVE')}
                      disabled={savingCR}
                    >
                      {savingCR ? 'Đang xử lý...' : `Duyệt batch (${selectedCRIds.size})`}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleBulkReviewCR('REJECT')}
                      disabled={savingCR}
                    >
                      {savingCR ? 'Đang xử lý...' : `Từ chối batch (${selectedCRIds.size})`}
                    </Button>
                  </>
                )}
              </div>

              {loadingCRs && (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {pendingCRCount > 0 && (
                <Alert variant="warning" className="py-2 small">
                  <b>{pendingCRCount} yêu cầu cũ</b> tạo từ trước ngày bỏ duyệt (#190405) vẫn còn
                  treo. Đây là dữ liệu cũ duy nhất còn cần thao tác tay — xử lý xong thì mọi nút
                  duyệt trên màn này biến mất.
                </Alert>
              )}

              {!loadingCRs && !changeRequests.length && (
                <Alert variant="info">Chưa có thay đổi ca ưu tiên nào được ghi lại.</Alert>
              )}

              {!loadingCRs && changeRequests.map((cr) => {
                const isPending = cr.status === 'PENDING';
                const isAutoApproved = cr.review_note?.startsWith('🤖');
                return (
                  <Card key={cr.id} className="mb-3">
                    <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
                      {isPending && (
                        <Form.Check
                          type="checkbox"
                          checked={selectedCRIds.has(cr.id)}
                          onChange={() => toggleCRSelect(cr.id)}
                          className="me-1"
                        />
                      )}
                      <b>{cr.employee?.full_name || cr.employee?.username || 'NV'}</b>
                      <Badge bg={cr.type === 'DELETE' ? 'danger' : cr.type === 'CREATE' ? 'success' : 'primary'}>
                        {cr.type === 'DELETE' ? 'XÓA' : cr.type === 'CREATE' ? 'THÊM' : 'SỬA'}
                      </Badge>
                      <Badge bg={isPending ? 'warning' : cr.status === 'APPROVED' ? 'success' : 'secondary'}>
                        {isPending ? 'Dữ liệu cũ chưa xử lý' : cr.status === 'APPROVED' ? 'Đã áp dụng' : 'Từ chối (cũ)'}
                      </Badge>
                      <span className="text-muted small ms-auto">
                        {new Date(cr.created_at).toLocaleString('vi-VN')}
                      </span>
                    </Card.Header>
                    <Card.Body>
                      {/* Ca hiện tại */}
                      {/* Ca ưu tiên liên quan — bản ghi đã xoá thì không còn liên kết, nội dung
                          nằm trong ảnh chụp `payload.before` do renderDiff() in ra bên dưới. */}
                      {cr.preference?.shift_template?.name && (
                        <div className="mb-2">
                          <span className="text-muted small">Ca hiện tại: </span>
                          <b>{cr.preference.shift_template.name}</b>
                          {cr.preference?.days_of_week?.length > 0 && (
                            <span className="ms-2">
                              {cr.preference.days_of_week.map((d) => weekdayLabels[d]).join(' ')}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Diff */}
                      <div className="mb-2">{renderDiff(cr)}</div>
                      {/* #190405: lý do là thứ THAY CHO bước duyệt — cho nó đứng nổi bật, không
                          còn là ghi chú phụ mờ nhạt như thời còn admin xét duyệt. */}
                      <div className={cr.reason ? 'small' : 'small text-muted fst-italic'}>
                        <b>Lý do:</b>{' '}
                        {cr.reason || '(bản ghi cũ không có lý do)'}
                      </div>
                      {/* Ai làm, lúc nào */}
                      {!isPending && (
                        <div className="mt-2 border-top pt-2 small text-muted">
                          {isAutoApproved
                            ? '🤖 Tự động duyệt (cơ chế cũ)'
                            : cr.payload?.applied_by_admin
                              ? 'Quản lý chỉnh trực tiếp'
                              : 'Nhân viên tự đổi (áp dụng ngay)'}
                          {cr.reviewed_at && ` • ${new Date(cr.reviewed_at).toLocaleString('vi-VN')}`}
                        </div>
                      )}
                    </Card.Body>
                    {/* Chỉ DỮ LIỆU CŨ mới còn nút duyệt (#190405). */}
                    {isPending && (
                      <Card.Footer>
                        <div className="small text-muted mb-2">
                          Bản ghi cũ từ trước khi bỏ duyệt — xử lý nốt để dọn hàng chờ.
                        </div>
                        <Form.Control
                          as="textarea"
                          rows={1}
                          placeholder="Ghi chú admin (tùy chọn)"
                          value={notesByCRId[cr.id] || ''}
                          onChange={(e) => setNotesByCRId((prev) => ({ ...prev, [cr.id]: e.target.value }))}
                          className="mb-2"
                          size="sm"
                        />
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => handleSingleReviewCR(cr.id, 'APPROVE')}
                            disabled={savingCR}
                          >
                            Duyệt
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleSingleReviewCR(cr.id, 'REJECT')}
                            disabled={savingCR}
                          >
                            Từ chối
                          </Button>
                        </div>
                      </Card.Footer>
                    )}
                  </Card>
                );
              })}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </PageLayout>
  );
}
